// Factory function pattern to avoid Webpack TDZ issues with class inheritance
// This uses BaseSuperAdminFactory instead of class inheritance

import { createBaseSuperAdmin, type IBaseSuperAdmin } from "../BaseSuperAdminFactory";
import type { Document } from "le-coffre-resources/dist/SuperAdmin";
import type { ApiPayload, QueryPayload } from "../../../types";
import type { IGetDocumentsparams, IPutDocumentsParams } from "./Documents";
import {
	buildDocumentsCreatePayload,
	buildDocumentsUpdatePayload,
} from "@Front/Api/LeCoffreApi/helpers/businessRequestPayloadHelpers";

interface DocumentsInstance {
	get(q: IGetDocumentsparams): Promise<Document[]>;
	post(body: ApiPayload): Promise<Document>;
	getByUid(uid: string, q?: QueryPayload): Promise<Document>;
	put(uid: string, body: IPutDocumentsParams): Promise<Document>;
	delete(uid: string): Promise<Document>;
}

// Module-level singleton instance
let instance: DocumentsInstance | null = null;
let baseSuperAdminInstance: IBaseSuperAdmin | null = null;

async function documentsGet(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string, q: IGetDocumentsparams): Promise<Document[]> {
	const url = new URL(baseUrl);
	if (q) baseSuperAdmin.appendJsonSearchParams(url, { q });
	try {
		return await baseSuperAdmin.getRequest<Document[]>(url);
	} catch (err) {
		baseSuperAdmin.onError(err);
		return Promise.reject(err);
	}
}

async function documentsPost(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string, body: ApiPayload): Promise<Document> {
	const url = new URL(baseUrl);
	try {
		return await baseSuperAdmin.postRequest<Document>(url, buildDocumentsCreatePayload(body));
	} catch (err) {
		baseSuperAdmin.onError(err);
		return Promise.reject(err);
	}
}

async function documentsGetByUid(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string, uid: string, q?: QueryPayload): Promise<Document> {
	const url = new URL(baseUrl.concat(`/${uid}`));
	if (q) baseSuperAdmin.appendJsonSearchParams(url, { q });
	try {
		return await baseSuperAdmin.getRequest<Document>(url);
	} catch (err) {
		baseSuperAdmin.onError(err);
		return Promise.reject(err);
	}
}

async function documentsPut(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string, uid: string, body: IPutDocumentsParams): Promise<Document> {
	const url = new URL(baseUrl.concat(`/${uid}`));
	try {
		return await baseSuperAdmin.putRequest<Document>(url, buildDocumentsUpdatePayload(body));
	} catch (err) {
		baseSuperAdmin.onError(err);
		return Promise.reject(err);
	}
}

async function documentsDelete(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string, uid: string): Promise<Document> {
	const url = new URL(baseUrl.concat(`/${uid}`));
	try {
		return await baseSuperAdmin.deleteRequest<Document>(url);
	} catch (err) {
		baseSuperAdmin.onError(err);
		return Promise.reject(err);
	}
}

function createDocuments(): DocumentsInstance {
	if (!baseSuperAdminInstance) {
		baseSuperAdminInstance = createBaseSuperAdmin();
	}
	const baseSuperAdmin = baseSuperAdminInstance;
	const baseUrl = baseSuperAdmin.namespaceUrl.concat("/documents");
	return {
		get: (q: IGetDocumentsparams) => documentsGet(baseSuperAdmin, baseUrl, q),
		post: (body: ApiPayload) => documentsPost(baseSuperAdmin, baseUrl, body),
		getByUid: (uid: string, q?: QueryPayload) => documentsGetByUid(baseSuperAdmin, baseUrl, uid, q),
		put: (uid: string, body: IPutDocumentsParams) => documentsPut(baseSuperAdmin, baseUrl, uid, body),
		delete: (uid: string) => documentsDelete(baseSuperAdmin, baseUrl, uid),
	};
}

export function getDocumentsInstance(): DocumentsInstance {
	if (!instance) {
		instance = createDocuments();
	}
	return instance;
}
